import React from "react";
import {
  Satellite,
  Map,
  FileText,
  BarChart3,
  CheckCircle,
  Cpu,
  Gauge,
  Truck,
} from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";

const InnovationTechnology: React.FC<{ content?: any }> = ({ content }) => {
  const { isArabic } = useLanguage();
  const systemsList = content?.systems || [
    {
      iconName: "Gauge",
      title: isArabic ? "نظام مراقبة المركبات (IVMS)" : "In-Vehicle Monitoring System (IVMS)",
      description: isArabic
        ? "يُراقب سلوك السائق والتزامه بالسلامة في الوقت الفعلي لتعزيز القيادة الآمنة وتجنب الحوادث."
        : "Monitors driver behavior, journey compliance, and safety events in real-time to promote defensive driving and zero-incident operations.",
    },
    {
      iconName: "Cpu",
      title: isArabic ? "نظام إدارة السائقين والأسطول (DFMS)" : "Driver & Fleet Management System (DFMS)",
      description: isArabic
        ? "يُشغل المواعيد والصيانة وسجلات السائقين آلياً لرفع الكفاءة التشغيلية والامتثال."
        : "Automates trip assignments, maintenance scheduling, and driver records to optimize operational efficiency and compliance.",
    },
    {
      iconName: "Satellite",
      title: isArabic ? "نظام مراقبة ضغط الإطارات (TPMS)" : "Tyre Pressure Monitoring System (TPMS)",
      description: isArabic
        ? "يوفر بيانات مباشرة عن صحة الإطارات وتنبيهات استباقية لتقليل الأعطال واستهلاك الوقود."
        : "Provides live tyre health data and predictive alerts to reduce breakdowns, fuel consumption, and environmental impact.",
    },
    {
      iconName: "BarChart3",
      title: isArabic ? "لوحة التحكم والتحليلات المركزية" : "Central Dashboard & Analytics",
      description: isArabic
        ? "تجمع التحليلات المباشرة كافة الأنظمة لمساعدة الإدارة في متابعة حالة الأسطول في سلطنة عمان."
        : "Live analytics integrate all systems — enabling management to visualize fleet status, utilization, and performance KPIs across Oman.",
    },
  ];

  const customerBenefitsList = content?.benefits || [
    { title: isArabic ? "دقة التسليم" : "Delivery Accuracy", desc: isArabic ? "تحسين دقة التسليم من خلال الرؤية المباشرة للأسطول." : "Improved delivery accuracy through real-time fleet visibility." },
    { title: isArabic ? "استجابة العملاء" : "Client Response", desc: isArabic ? "استجابة وسرعة أكبر في التواصل بين العمليات والعملاء." : "Faster response and communication between operations and clients." },
    { title: isArabic ? "رؤى السلامة" : "Safety Insights", desc: isArabic ? "رؤى قائمة على البيانات لتعزيز السلامة والاستدامة والموثوقية." : "Data-driven insights enhance safety, sustainability, and reliability." },
  ];

  const iconMap: Record<string, any> = { Gauge, Cpu, Satellite, BarChart3, CheckCircle, Truck, Map };

  return (
    <section id="innovation" className="py-20 bg-slate-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <span className="text-red-600 font-black text-xs uppercase tracking-[0.3em] mb-4 block">Future Forward</span>
          <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight">
            {content?.title || "Innovation & Technology"}
          </h2>
          <div className="w-20 h-1.5 bg-red-600 mx-auto rounded-full mb-8" />
          <p className="mt-6 text-slate-500 max-w-4xl mx-auto text-[16px] md:text-[18px] leading-relaxed font-medium">
            {content?.desc || "HTD continuously invests in innovative digital systems to ensure the highest standards of safety, transparency, and efficiency in every journey."}
          </p>
        </motion.div>

        {/* Features Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mb-24">
          {systemsList.map((system: any, index: number) => {
            const Icon = iconMap[system.iconName] || Gauge;
            return (
              <motion.div
                key={index}
                className="bg-white rounded-[2.5rem] p-10 text-center shadow-xl hover:shadow-2xl transition-all duration-500 border border-slate-100 group relative overflow-hidden"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-red-600/5 rounded-full translate-x-16 -translate-y-16 group-hover:scale-150 transition-transform duration-700" />
                <div className="flex items-center justify-center w-20 h-20 mx-auto rounded-[2rem] bg-slate-900 mb-8 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                  <Icon className="w-10 h-10 text-red-500" />
                </div>
                <h3 className="text-[17px] font-black text-slate-900 mb-4 tracking-tight group-hover:text-red-600 transition-colors uppercase">{system.title}</h3>
                <p className="text-slate-500 text-[15px] leading-relaxed font-medium">
                  {system.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Real Dashboard & Fleet Images */}
        <motion.div
          className="mb-24 flex justify-center"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
        >
          <div className="relative bg-white shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] rounded-[3rem] overflow-hidden w-full max-w-6xl p-4 md:p-8 border border-slate-100">
            <div className="bg-slate-100/50 h-12 w-full rounded-2xl flex items-center justify-start px-6 gap-3 mb-6">
              <div className="w-3 h-3 bg-red-500 rounded-full shadow-lg shadow-red-500/50" />
              <div className="w-3 h-3 bg-amber-400 rounded-full" />
              <div className="w-3 h-3 bg-emerald-500 rounded-full" />
              <div className="ml-4 h-4 w-48 bg-slate-200 rounded-full" />
            </div>
            <div className="relative rounded-[2rem] overflow-hidden shadow-inner bg-slate-900">
              <img
                src={content?.image || "/tracking-system1.png"}
                alt="HTD Real-Time Dashboard"
                className="w-full h-auto min-h-[400px] md:h-[600px] object-cover opacity-90 hover:opacity-100 transition-opacity duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent pointer-events-none" />
              <div className="absolute bottom-10 left-10 text-white">
                <p className="text-xs font-black uppercase tracking-widest text-red-500 mb-2">Live Fleet Control</p>
                <p className="text-2xl font-bold tracking-tight">Oman Central Dashboard</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Customer Advantages */}
        <motion.div
          className="mt-20"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="text-center mb-16">
            <span className="text-slate-400 font-black text-[10px] uppercase tracking-[0.4em] mb-4 block">
              {content?.strategicLabel || "Strategic Value"}
            </span>
            <h3 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">
              {content?.benefitTitle || "Customer Advantages"}
            </h3>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {customerBenefitsList.map((benefit: any, index: number) => (
              <motion.div
                key={index}
                className="bg-white p-10 rounded-[2.5rem] shadow-xl hover:shadow-2xl transition-all duration-500 border border-slate-100 flex flex-col items-center group overflow-hidden relative"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                 <div className="absolute top-0 left-0 w-full h-1.5 bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-red-50 mb-8 group-hover:bg-red-600 transition-colors duration-500">
                  <CheckCircle className="w-8 h-8 text-red-600 group-hover:text-white transition-colors duration-500" />
                </div>
                <h4 className="text-slate-900 text-[18px] leading-relaxed font-black text-center mb-3 tracking-tight uppercase">
                  {benefit.title || benefit}
                </h4>
                {benefit.desc && (
                  <p className="text-slate-500 text-[15px] leading-relaxed font-medium text-center">
                    {benefit.desc}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InnovationTechnology;
